/*
 * <variant-picker>: keeps the buy box in step with the chosen variant.
 *
 * The swatch radios (or the select) are named "id", so the form already posts
 * the right variant without this script. With it, a change also updates the
 * legend, the section's price, the add-to-cart button, the dynamic checkout,
 * the gallery image and, on the product template, ?variant= in the address
 * bar. It then dispatches `variant:change` on document with
 * { variant, sectionId, formId } for the CTA rail and anything else listening.
 */
(() => {
  'use strict';

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.variants = this.readVariants();
      this.form = document.getElementById(this.dataset.formId) || this.closest('form');
      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    readVariants() {
      const script = this.querySelector('script[data-variants]');
      if (!script) return [];
      try {
        const data = JSON.parse(script.textContent);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        return [];
      }
    }

    onChange(event) {
      const input = event.target;
      // The hidden mirror is disabled, so its own change event is ignored here.
      if (!input || input.name !== 'id' || input.disabled) return;
      const variant = this.variants.find((item) => String(item.id) === String(input.value));
      if (variant) this.select(variant);
    }

    select(variant) {
      const scope = this.closest('.shopify-section') || document;

      this.updateMirror(variant);
      this.updateLegend(variant);
      this.updatePrice(scope, variant);
      this.updateButtons(variant);
      this.updateUrl(variant);
      this.showMedia(scope, variant.featured_media);

      document.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant, sectionId: this.dataset.sectionId, formId: this.dataset.formId },
        })
      );
    }

    // Scripts that read the form's first [name="id"] (or listen for its change)
    // see the chosen variant; the checked radio is still what gets posted.
    updateMirror(variant) {
      const mirror = this.querySelector('[data-variant-mirror]');
      if (!mirror) return;
      mirror.value = variant.id;
      mirror.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateLegend(variant) {
      const options = Array.isArray(variant.options) ? variant.options : [];
      const value = options.length === 1 ? options[0] : variant.title;
      this.querySelectorAll('[data-selected-value]').forEach((el) => {
        el.textContent = value;
      });
    }

    updatePrice(scope, variant) {
      scope.querySelectorAll('[data-price-current]').forEach((el) => {
        el.textContent = variant.price;
      });
      // Compare-at prices are never in the variant data, so a rendered one is
      // hidden on change rather than left next to a price it may not belong to.
      scope.querySelectorAll('[data-price-compare]').forEach((el) => {
        el.hidden = true;
      });
    }

    updateButtons(variant) {
      if (!this.form) return;
      this.form.querySelectorAll('[data-add-button]').forEach((button) => {
        button.disabled = !variant.available;
        const label = button.querySelector('[data-add-label]');
        const text = variant.available ? button.dataset.labelAdd : button.dataset.labelSoldOut;
        if (label && text) label.textContent = text;
      });
      this.form.querySelectorAll('[data-dynamic-checkout]').forEach((el) => {
        el.hidden = !variant.available;
      });
    }

    updateUrl(variant) {
      if (!this.hasAttribute('data-update-url')) return;
      // Keep the other parameters (utm_*, fbclid, ttclid) that ads attribution reads.
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (error) {
        // A sandboxed or opaque-origin frame refuses the URL update; nothing else depends on it.
      }
    }

    showMedia(scope, mediaId) {
      if (mediaId === null || mediaId === undefined) return;
      const gallery = scope.querySelector('media-gallery');
      const track = gallery && gallery.querySelector('[data-gallery-track]');
      if (!track || typeof gallery.goTo !== 'function') return;
      const index = [...track.children].findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1) gallery.goTo(index);
    }
  }

  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
})();
